import { getBaseRole } from '@/helpers/customRoles'
import { FC, useEffect, useMemo, useState } from 'react'
import RequiredEmailConfirmationModal from './components/RequiredEmailConfirmationModal/RequiredEmailConfirmationModal'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'

const isEnabledFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === '1'

const isEmailConfirmed = (user: any): boolean =>
  isEnabledFlag(user?.is_email_confirmed) ||
  isEnabledFlag(user?.isEmailConfirmed)

const isSettingEnabled = (value: unknown): boolean =>
  value === true || value === 1 || value === '1'

/**
 * Как в исходной модалке:
 * settings по роли + неподтверждённая почта.
 * Отдельный user/view не дергаем — is_email_confirmed уже приходит
 * из Technical / профиля в redux.
 */
const needsConfirmationBySettings = (
  profile: any,
  settings: any,
): boolean => {
  if (!profile?.id || isEmailConfirmed(profile)) return false

  const baseRole = getBaseRole(profile.role)
  const {
    requireParentEmailConfirmation,
    requireStudentEmailConfirmation,
    requireEmployeeEmailConfirmation,
  } = settings ?? {}

  if (baseRole === 'children') {
    return isSettingEnabled(requireStudentEmailConfirmation)
  }
  if (baseRole === 'parent') {
    return isSettingEnabled(requireParentEmailConfirmation)
  }
  return isSettingEnabled(requireEmployeeEmailConfirmation)
}

const shouldShowEmailConfirmation = (
  user: any,
  settings: any,
): boolean => {
  if (!user?.id || isEmailConfirmed(user)) return false

  // requireEmailConfirmation приходит только в ответе login (AuthController),
  // на user/view его нет. После логина флаг залипает в profile/localStorage
  // и через actionUpdProfile не затирается — из‑за этого модалка могла
  // открываться при выключенном requireEmployeeEmailConfirmation.
  // Источник истины — настройки платформы по роли.
  return needsConfirmationBySettings(user, settings)
}

const isAdminRole = (role: unknown) => getBaseRole(role) === 'admin'

const RequiredEmailConfirmation: FC = () => {
  const [verifyEmail, setVerifyEmail] = useState(false)
  // Ключ «уже закрыл» — сбрасывается при смене email / статуса / пользователя
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)

  const profile = useSelector(selectProfile)
  const settings = useSelector((store: any) => store.settings)
  const settingsStatus = useSelector((store: any) => store.settings?.status)
  const loadingSettings = (settingsStatus ?? 0) < 2
  const canDismiss = isAdminRole(profile?.role)

  const dismissKey = useMemo(() => {
    if (!profile?.id) return null
    return [
      profile.id,
      String(profile.email ?? ''),
      isEmailConfirmed(profile) ? '1' : '0',
    ].join(':')
  }, [
    profile?.id,
    profile?.email,
    profile?.is_email_confirmed,
    profile?.isEmailConfirmed,
  ])

  useEffect(() => {
    if (!profile?.id) {
      setVerifyEmail(false)
      setDismissedKey(null)
      return
    }
    if (loadingSettings) return

    if (isEmailConfirmed(profile)) {
      setVerifyEmail(false)
      return
    }

    if (canDismiss && dismissKey && dismissedKey === dismissKey) {
      setVerifyEmail(false)
      return
    }

    setVerifyEmail(shouldShowEmailConfirmation(profile, settings))
  }, [
    canDismiss,
    dismissKey,
    dismissedKey,
    loadingSettings,
    profile,
    settings,
  ])

  return (
    <RequiredEmailConfirmationModal
      open={verifyEmail}
      canDismiss={canDismiss}
      onClose={() => {
        if (!canDismiss || !dismissKey) return
        setDismissedKey(dismissKey)
        setVerifyEmail(false)
      }}
      onSuccess={() => {
        setDismissedKey(null)
        setVerifyEmail(false)
      }}
    />
  )
}

export default RequiredEmailConfirmation
